/**
 * Typed surface over the vendored inference measurements
 * (`vendor/cdt-infer.browser.js`, exposed as `globalThis.CDTInfer`). The
 * measurement code is a verbatim slice of the upstream `infer.js`, verified
 * to produce identical JSON — this file only names its report shape.
 */
import "./vendor/cdt-resolve.bundle.js";
import "./vendor/cdt-infer.browser.js";
import type { ResolverCanvas, ResolverItemsDoc } from "./cdtResolve";

export interface InferenceLevel {
  depth: number;
  attr: string;
  label: string;
  declared: "horizontal" | "vertical";
  measured: "horizontal" | "vertical";
  pairs: number;
  blocks: number;
  xOnly: number;
  yOnly: number;
  both: number;
  neither: number;
  agrees: boolean;
  /** Blocks that build this level the opposite way from the level as a whole. */
  contrary: readonly { key: string; x: number; y: number; pairs: number }[];
}

export interface InferenceOrder {
  attr: string;
  label: string;
  vertical: boolean;
  isSize: boolean;
  conc: number;
  disc: number;
  agreement: number | null;
  worst: readonly { pair: string; n: number }[];
}

export interface InferenceCandidates {
  attr: string;
  label: string;
  vorder: number | null;
  pins: number | null;
  magnitude: number | null;
}

export interface InferenceGradientSection {
  sec: string;
  rows: readonly { shelf: number; h: number }[];
  ok: number;
  bad: number;
}

export interface InferenceReport {
  levels: readonly InferenceLevel[];
  order: readonly InferenceOrder[];
  candidates: readonly InferenceCandidates[];
  gradient: {
    byRun: readonly {
      run: number;
      rows: readonly { shelf: number; n: number; h: number; pitch: number }[];
      monotoneSteps: number;
      counterSteps: number;
    }[];
    sections: readonly InferenceGradientSection[];
    inversions: unknown;
  };
  sizes: {
    total: number;
    unparsed: readonly { id: string; label: string; h: number; w: number }[];
    mixedBlocks: readonly { node: string; families: Record<string, number>; n: number }[];
    storedListHead: readonly string[];
  };
  pins: readonly { scope: string; attr: string; label: string; back: number; seen: string[] }[];
  notes: readonly string[];
}

interface CdtInferGlobal {
  infer: (canvas: ResolverCanvas, itemsDoc: ResolverItemsDoc, deps: unknown) => InferenceReport;
}

export const inferCdt = (canvas: ResolverCanvas, itemsDoc: ResolverItemsDoc): InferenceReport => {
  const g = globalThis as { CDTInfer?: CdtInferGlobal; CDTResolve?: Record<string, unknown> };
  if (!g.CDTInfer || !g.CDTResolve) throw new Error("cdt-infer bundle did not attach");
  return g.CDTInfer.infer(canvas, itemsDoc, {
    loadPlan: g.CDTResolve.loadPlan,
    Plan: g.CDTResolve.Plan,
    grade: g.CDTResolve.grade,
  });
};
